// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import updatePickUpWarehouse from "@insite/client-framework/Store/Context/Handlers/UpdatePickUpWarehouse";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import FindLocationModal from "@insite/content-library/Components/FindLocationModal";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngUtilities from "@plumberssupply/App/Components/Common/XngUtilities";
import XngZone from "@plumberssupply/App/Components/Common/XngZone";
import Feedback from "@plumberssupply/App/Components/Feedback/Feedback";
import NewJobListsModal from "@plumberssupply/App/Components/NewJobListsModal/NewJobListsModal";
import xHeaderUpdate from "@plumberssupply/App/Handlers/XNG/Header";
import useSize, { useElementSize } from "@plumberssupply/App/Hooks/useSize";
import { CustomAppState } from "@plumberssupply/App/Store/Reducers";
import { getIsCheckoutPage } from "@plumberssupply/App/Store/Selectors/GeneralSelectors";
import FulfillmentMethodChangeModal from "@plumberssupply/App/Widgets/FulfillmentMethodAndCustomerLinks/FulfillmentMethodChangeModal";
import { constants, DivWrapper, SystemBuilderKey } from "@plumberssupply/XDL/constants";
import {
    color,
    hideElement,
    hideElementOnPrint,
    layoutContainer,
    xClassNames,
    xIsAuthenticated,
    xScreen,
} from "@plumberssupply/XDL/Utilities";
import React, { useEffect, useRef } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    siteName: state.context.website.name,
    session: getSession(state),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
    // XNG-Changes
    isLaptopScreen: state.xCommon.isLaptopScreen,
    registerMenu: state.xHeader.registerMenu,
    isSticky: state.xHeader.isSticky,
    currentLocation: getLocation(state),
    isCheckoutPage: getIsCheckoutPage(state),
    showGlobalMessage: state.xHeader.showGlobalMessage,
    fulfillmentMethodModalOpen: state.xHeader.fulfillmentMethodModalOpen,
    findLocationModalOpen: state.xHeader.findLocationModalOpen,
    isAuthenticated: xIsAuthenticated(state),
});

const mapDispatchToProps = {
    dismissRfqUpdates,
    xHeaderUpdate,
    updatePickUpWarehouse,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    searchContainer?: InjectableCss;
    actionsContainer?: InjectableCss;
    logoContainer?: InjectableCss;
    mainNavigationContainer?: InjectableCss;
    headerEmpty?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    page: {
        css: css`
            background-color: ${getColor("common.white")};
            width: 100%;
            position: relative;
            z-index: ${getProp("theme.zIndex.header")};

            ${({ showGlobalMessage }: any) =>
                !showGlobalMessage &&
                css`
                    box-shadow: 0 1px 0 0 ${color("common.ui7")};
                `}

            &:not(.in-shell) {
                &.checkout-page {
                    position: sticky;
                    top: 0;
                }

                ${xScreen("lte", constants.screen.laptopScreen)} {
                    position: sticky;
                    top: 0;
                }
            }
        `,
    },
    websiteName: {
        css: css`
            font-size: 12px;
            padding-left: 15px;
            display: none;
            @media print {
                display: block;
            }
        `,
    },
    headerEmpty: {
        css: css`
            background-color: ${color("primary")};
            ${hideElementOnPrint};
        `,
    },
    container: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 12px 20px;
            ${layoutContainer}

            &&& {
                padding-top: 10px;
                padding-bottom: 10px;
            }

            ${xScreen("lte", 767)} {
                flex-wrap: wrap;
            }
        `,
    },
    logoContainer: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: flex-start;
            column-gap: 20px;
            order: 1;
            ${xScreen("lte", 360)} {
                column-gap: 15px;
            }
            &.checkout-header > div > div {
                ${xScreen(769, 1024)} {
                    max-width: 198px;
                    width: 198px;
                    height: 45px;
                    img {
                        height: 45px;
                        width: auto;
                        max-width: 100%;
                        max-height: 100%;
                    }
                }
            }
        `,
    },
    searchContainer: {
        css: css`
            flex-grow: 1;
            order: 2;
            max-width: 528px;

            ${xScreen("lte", "sm")} {
                max-width: 360px;
            }
            ${xScreen("lte", 767)} {
                width: 100%;
                max-width: 100%;
                order: 3;
            }
        `,
    },
    actionsContainer: {
        css: css`
            order: 3;
            ${xScreen("lte", 767)} {
                order: 2;
            }

            & > ${DivWrapper} {
                display: flex;
                align-items: center;
                gap: 36px;
            }

            ${xScreen("lte", constants.screen.laptopScreen)} {
                & > ${DivWrapper} {
                    gap: 12px;
                }
            }
        `,
    },
    mainNavigationContainer: {
        css: css`
            width: 100%;
            background-color: ${color("common.white")};
            box-shadow: inset 0 1px 0 0 ${color("common.ui7")};
        `,
    },
};

const styles = headerStyles;

const Header = ({
    id,
    siteName,
    session,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    // XNG-Changes
    isLaptopScreen,
    currentLocation,
    isSticky,
    xHeaderUpdate,
    isCheckoutPage,
    showGlobalMessage,
    fulfillmentMethodModalOpen,
    findLocationModalOpen,
    updatePickUpWarehouse,
    isAuthenticated,
}: Props) => {
    const headerRef = useRef<HTMLDivElement>(null);
    const headerWidth = typeof document !== "undefined" ? useElementSize(document.body)?.width : "100%";
    const headerHeight = useSize(headerRef)?.height;
    const { isInShell } = useShellContext();

    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    useEffect(() => {
        if (!isAuthenticated) {
            window.localStorage.removeItem(SystemBuilderKey);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    useEffect(() => {
        if (headerHeight) {
            xHeaderUpdate({ key: "headerHeight", value: `${headerHeight}px` });
        }
    }, [headerHeight]);

    // Hide main navigation
    useEffect(() => {
        setTimeout(() => hideAll());

        xHeaderUpdate({ key: "unregisterMenu", value: "main_navigation_menu" });
        document.querySelector("nav[class^='MenuWrapper-'].active")?.classList.remove("active");
    }, [isSticky, currentLocation]);

    const afterHeader = React.createRef<HTMLSpanElement>();

    const showEmptyHeader = isLaptopScreen || isCheckoutPage;

    // XNG-Changes
    const onFulfillmentMethodClose = () => {
        xHeaderUpdate({ key: "fulfillmentMethodModalOpen", value: false });
    };
    const onFindLocationModalClose = () => {
        xHeaderUpdate({ key: "findLocationModalOpen", value: false });
    };
    const toasterContext = React.useContext(ToasterContext);

    const handleWarehouseSelected = async (pickUpWarehouse: WarehouseModel) => {
        await updatePickUpWarehouse({ pickUpWarehouse });
        toasterContext.addToast({
            body: translate("Pick Up Address Updated"),
            messageType: "success",
        });
        onFindLocationModalClose();
    };

    return (
        <>
            <StyledWrapper {...styles.websiteName}>
                <Zone contentId={id} zoneName="Logo" />
            </StyledWrapper>
            <Page
                as="header"
                isHeader={true}
                {...styles.page}
                className={xClassNames.append([isCheckoutPage && "checkout-page", isInShell && "in-shell"])}
                {...{
                    showGlobalMessage: isCheckoutPage ? false : showGlobalMessage,
                }}
            >
                {/* {siteName && <Typography {...styles.websiteName}>{siteName}</Typography>} */}
                {/* {!isInShell && showEmptyHeader && (
                    <StyledWrapper
                        className={xClassNames.append(["header-emptyDiv", isCheckoutPage && "show"])}
                        {...styles.headerEmpty}
                        style={{ height: headerHeight, maxWidth: headerWidth }}
                    />
                )} */}
                <StyledWrapper
                    ref={headerRef}
                    {...styles.page}
                    id="header-page"
                    {...{
                        isInShell,
                        maxWidth: headerWidth,
                        showGlobalMessage: isCheckoutPage ? false : showGlobalMessage,
                    }}
                >
                    <SkipNav
                        extendedStyles={styles.skipToContent}
                        text={translate("Skip to main content")}
                        destination={afterHeader}
                    />
                    <EagerLoadingLogoProvider value={true}>
                        {isCheckoutPage ? (
                            <StyledWrapper {...styles.container}>
                                <StyledWrapper
                                    {...styles.logoContainer}
                                    className="checkout-header"
                                    data-test-selector="header-logoSection"
                                >
                                    <XngZone contentId={id} zoneName="Logo" normalWidth={true} />
                                </StyledWrapper>
                            </StyledWrapper>
                        ) : (
                            <>
                                <XngZone
                                    contentId={id}
                                    zoneName="secondaryNavigation"
                                    normalWidth={true}
                                    hint="secondaryNavigation"
                                />
                                <StyledWrapper {...styles.container}>
                                    <StyledWrapper
                                        {...styles.logoContainer}
                                        id="header-logoSection"
                                        data-test-selector="header-logoSection"
                                    >
                                        {isLaptopScreen && (
                                            <XngZone contentId={id} zoneName="MainNavigation" normalWidth={true} />
                                        )}
                                        <XngZone contentId={id} zoneName="Logo" normalWidth={true} />
                                    </StyledWrapper>
                                    <StyledWrapper
                                        {...styles.searchContainer}
                                        data-test-selector="header-searchSection"
                                    >
                                        <XngZone contentId={id} zoneName="Search" normalWidth={true} />
                                    </StyledWrapper>
                                    <StyledWrapper
                                        {...styles.actionsContainer}
                                        data-test-selector="header-actionSection"
                                    >
                                        <XngZone contentId={id} zoneName="Actions" normalWidth={true} />
                                    </StyledWrapper>
                                </StyledWrapper>
                                {!isLaptopScreen && (
                                    <StyledWrapper {...styles.mainNavigationContainer}>
                                        <XngZone contentId={id} zoneName="MainNavigation" normalWidth={false} />
                                    </StyledWrapper>
                                )}
                                <XngZone contentId={id} zoneName="GlobalMessages" />
                                <XngZone contentId={id} zoneName="Scripts" />
                                <XngZone contentId={id} zoneName="Breadcrumb" />
                            </>
                        )}
                    </EagerLoadingLogoProvider>
                    <span ref={afterHeader} tabIndex={-1} />
                </StyledWrapper>
                <FulfillmentMethodChangeModal
                    isOpen={fulfillmentMethodModalOpen}
                    onFulfillmentMethodClose={onFulfillmentMethodClose}
                />
                <FindLocationModal
                    modalIsOpen={findLocationModalOpen}
                    onWarehouseSelected={handleWarehouseSelected}
                    onModalClose={onFindLocationModalClose}
                />
                <NewJobListsModal />
            </Page>
            <XngUtilities />
            <Feedback />
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
